/**
 * frontend/src/features/telephony/hooks/useQueuePresenceSSE.js
 * -------------------------------------------------------------
 * Custom hook that opens a Server-Sent Events connection to
 * GET /telephony/presence/stream and calls `onUpdate` whenever
 * the backend pushes a queue_update event.
 *
 * Event flow (backend → frontend):
 *   Exotel webhook fires (call answered / ended / missed)
 *     → _write_agent_presence() writes to Redis
 *       → telephony_sse_manager.broadcast_sync() pushes to all queues
 *         → this EventSource receives the event (<100ms end-to-end)
 *           → onUpdate(parsedData) called
 *             → ManagerQueueMonitorCard re-fetches the snapshot (force=true)
 *
 * Auth design:
 *   Browser EventSource cannot send custom HTTP Authorization headers.
 *   Matches the existing project pattern (DashboardLayout.jsx resolveChatWsUrl)
 *   by passing authToken as a query parameter (?token=...).
 *
 * @param {(data: object) => void} onUpdate  Called on every 'queue_update' SSE event
 * @param {boolean} [enabled=true]           Set false to skip SSE (e.g. user not on Call Center page)
 * @returns {{ sseConnected: boolean }}      Whether SSE is currently active
 */
import { useEffect, useRef, useState, useCallback } from 'react'

const API_BASE = String(import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000').trim().replace(/\/+$/, '')

function resolvePresenceSseUrl() {
  const token = localStorage.getItem('authToken')
  if (!token) return ''
  return `${API_BASE}/telephony/presence/stream?token=${encodeURIComponent(token)}`
}

export default function useQueuePresenceSSE(onUpdate, enabled = true) {
  const [sseConnected, setSseConnected] = useState(false)
  const esRef = useRef(null)
  const onUpdateRef = useRef(onUpdate)

  // Keep callback ref in sync without re-triggering the effect
  useEffect(() => {
    onUpdateRef.current = onUpdate
  }, [onUpdate])

  const connect = useCallback(() => {
    if (esRef.current) {
      esRef.current.close()
      esRef.current = null
    }

    const sseUrl = resolvePresenceSseUrl()
    if (!sseUrl) {
      setSseConnected(false)
      return null
    }

    const es = new EventSource(sseUrl)
    esRef.current = es

    es.addEventListener('queue_update', (event) => {
      try {
        const data = JSON.parse(event.data)
        onUpdateRef.current?.(data)
      } catch (err) {
        console.warn('[SSE] Failed to parse queue_update event:', err)
      }
    })

    es.onopen = () => {
      setSseConnected(true)
      console.debug('[SSE] ✓ Connected to /telephony/presence/stream')
    }

    es.onerror = () => {
      setSseConnected(false)
      console.debug('[SSE] Connection interrupted — retrying')
    }

    return es
  }, [])

  useEffect(() => {
    if (!enabled) {
      setSseConnected(false)
      return
    }

    const es = connect()

    return () => {
      if (es) es.close()
      setSseConnected(false)
    }
  }, [enabled, connect])

  return { sseConnected }
}
